import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Browser, GoogleLogo, Lightning, BellRinging, ChatCircleDots, PhoneSlash } from "@phosphor-icons/react";
import { inquiryHref, selectInterest } from "@/lib/inquiry";
import { PRICES } from "@/lib/visibility";

const SOLUTIONS = [
  {
    id: "websites",
    Icon: Browser,
    title: "Websites",
    promise: "Built, hosted, and kept current — so you never have to think about it.",
    body: "A mobile-first site built around your services and the way customers actually reach you. We host it, maintain it, and give you a private portal to update hours, photos, and services yourself.",
    offers: [
      { id: "websites", name: "Website design, hosting & management", price: "$499 build + $79/mo", href: "#pricing", cta: "See the plan" },
      { id: "improve", anchor: "improve", name: "Improve an existing website", price: "Free review · quoted per project", href: "/website-review", cta: "Request a free review" },
    ],
  },
  {
    id: "google",
    Icon: GoogleLogo,
    title: "Google presence",
    promise: "Where most local customers look first — set up right, then kept active.",
    body: "A complete, accurate Google Business Profile done once. Or keep it active every month with posts made from your own job photos and clips — captioned, lightly edited, and published to Google and Facebook or Instagram.",
    offers: [
      { id: "gbp", name: "Google Business Profile setup or cleanup", price: `$${PRICES.gbpSetup} one-time · per location`, href: "/google-business-profile", cta: "What's included" },
      { id: "visibility", name: "Local Visibility", price: `$${PRICES.onboarding} onboarding + $${PRICES.monthly}/mo`, href: "/local-visibility", cta: "How it works" },
    ],
  },
  {
    id: "automations",
    Icon: Lightning,
    title: "Quote & call automations",
    promise: "Answer new inquiries while you're still on a job.",
    body: "Optional automations added to a site we built or a compatible existing site. Each one is quoted individually after we check your forms and phone setup.",
    offers: [
      { id: "automations", anchor: "automations", name: "Quote & call automations", price: "Optional · quoted individually", href: inquiryHref("automations"), onClick: () => selectInterest("automations"), cta: "Ask about automations" },
    ],
    bullets: [
      { Icon: BellRinging, who: "To you", text: "Text alerts the moment a quote request arrives" },
      { Icon: ChatCircleDots, who: "To your customer", text: "Automatic email & text acknowledgments" },
      { Icon: PhoneSlash, who: "To the caller", text: "Missed-call text backs when you can't pick up" },
    ],
  },
];

function ItemLink({ href, onClick, className, children, ...rest }) {
  return href.startsWith("#") ? (
    <a href={href} onClick={onClick} className={className} {...rest}>{children}</a>
  ) : (
    <Link to={href} onClick={onClick} className={className} {...rest}>{children}</Link>
  );
}

function OfferRow({ offer }) {
  return (
    <div
      id={offer.anchor}
      data-testid={`service-card-${offer.id}`}
      className="group flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-6 py-4 scroll-mt-28"
    >
      <div className="min-w-0">
        <div className="text-[15px] text-zinc-900 leading-snug">{offer.name}</div>
        <div data-testid={`service-price-${offer.id}`} className="mt-0.5 text-[13px] text-zinc-600">{offer.price}</div>
      </div>
      <ItemLink
        href={offer.href}
        onClick={offer.onClick}
        data-testid={`service-cta-${offer.id}`}
        className="inline-flex items-center gap-1.5 text-[13.5px] font-medium text-brand-700 hover:text-brand-500 transition-colors whitespace-nowrap"
      >
        {offer.cta}
        <ArrowUpRight size={13} weight="bold" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </ItemLink>
    </div>
  );
}

function SolutionCard({ s, index, wide }) {
  return (
    <motion.article
      data-testid={`solution-${s.id}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className={`card-light rounded-[28px] p-6 sm:p-9 flex flex-col ${wide ? "lg:col-span-2" : ""}`}
    >
      <div className={wide ? "grid md:grid-cols-2 gap-8 md:gap-12" : "flex flex-col flex-1"}>
        <div className="flex flex-col">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[12px] tracking-[0.2em] text-brand-700">0{index + 1}</span>
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-brand-50 text-brand-700">
              <s.Icon size={20} weight="regular" />
            </span>
          </div>
          <h3 className="mt-6 font-display font-bold text-[26px] sm:text-[32px] leading-[1.05] tracking-tight text-zinc-900">{s.title}</h3>
          <p className="mt-3 text-[15.5px] text-zinc-900 font-medium leading-snug">{s.promise}</p>
          <p className="mt-3 text-[14.5px] text-zinc-600 leading-relaxed">{s.body}</p>
          <div className={`mt-6 border-t border-zinc-200 divide-y divide-zinc-200 ${wide ? "" : "mt-auto pt-0"}`}>
            {s.offers.map((o) => <OfferRow key={o.id} offer={o} />)}
          </div>
        </div>

        {s.bullets && (
          <div className="rounded-2xl bg-[#f6f5f1] border border-zinc-200 p-5 sm:p-6 self-start md:self-stretch">
            <div className="text-[11px] tracking-[0.22em] uppercase text-zinc-600">Three separate automations</div>
            <ul className="mt-4 grid gap-4">
              {s.bullets.map(({ Icon, who, text }, i) => (
                <li key={who} data-testid={`service-automation-${i}`} className="flex items-start gap-3">
                  <span className="mt-[1px] inline-flex items-center justify-center w-7 h-7 rounded-lg bg-white border border-zinc-200 text-zinc-900 flex-shrink-0">
                    <Icon size={14} weight="regular" />
                  </span>
                  <span className="text-[14px] text-zinc-800 leading-relaxed">
                    {text}
                    <span className="ml-2 inline-flex items-center rounded-full border border-brand-100 bg-brand-50 px-2 py-[1px] text-[10.5px] tracking-[0.12em] uppercase text-brand-700 whitespace-nowrap align-middle">
                      {who}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
            <p data-testid="service-automations-note" className="mt-5 text-[12.5px] text-zinc-600 leading-relaxed">
              Acknowledgments confirm a request was received — they don&apos;t send a price or book an appointment.
            </p>
          </div>
        )}
      </div>
    </motion.article>
  );
}

export default function ServicesOverview() {
  return (
    <section
      id="services"
      data-tone="light"
      data-testid="services-overview"
      className="tone-light relative py-16 sm:py-24 md:py-32 bg-[#f6f5f1] scroll-mt-16 md:scroll-mt-20"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="text-[11.5px] tracking-[0.22em] uppercase text-brand-700 flex items-center gap-3">
              <span className="block w-8 h-px bg-brand-700" />
              Services
            </div>
            <h2 className="mt-5 font-display font-bold text-[34px] sm:text-5xl lg:text-[60px] leading-[1.02] tracking-tight text-zinc-900">
              What we build &amp; what it costs.
            </h2>
          </div>
          <p className="lg:col-span-4 text-zinc-600 text-[15px] sm:text-base leading-relaxed lg:pb-2">
            Five services for local businesses. Take one or combine them — each is priced and billed on its own, with no bundles required.
          </p>
        </div>

        <div className="mt-10 sm:mt-14 grid lg:grid-cols-2 gap-5 sm:gap-6">
          {SOLUTIONS.map((s, i) => (
            <SolutionCard key={s.id} s={s} index={i} wide={i === SOLUTIONS.length - 1} />
          ))}
        </div>
      </div>
    </section>
  );
}
